import { PartImageItem, PartImageItemBox } from '@/api/generatedApi';
import { CropRect, LassoPoint, SegmentationData } from '@/components/modals/ImageEditorModal/types';
import {
  outlineOfSegments,
  PartImageSegments,
  segmentsFromLabels,
} from '@physna/core/domain/part-image-identification/part-image-segments';
import { clampToPhoto } from './snapPartImageItems';

export interface EditCropPhoto {
  photoNumber: number;
  file: File;
  box: PartImageItemBox;
}

export interface NaturalSize {
  width: number;
  height: number;
}

const unionOf = (boxes: PartImageItemBox[]): PartImageItemBox => {
  const left = Math.min(...boxes.map(box => box.left));
  const top = Math.min(...boxes.map(box => box.top));
  const right = Math.max(...boxes.map(box => box.left + box.width));
  const bottom = Math.max(...boxes.map(box => box.top + box.height));
  return { left, top, width: right - left, height: bottom - top };
};

const hasArea = (box: PartImageItemBox): boolean => box.width > 0 && box.height > 0;

export function editCropPhotos(item: PartImageItem | undefined, images: File[]): EditCropPhoto[] {
  const regions = item?.regions ?? [];
  const photoNumbers = [...new Set(regions.map(region => region.photoNumber))];
  return photoNumbers.flatMap(photoNumber => {
    const file = images[photoNumber - 1];
    const box = unionOf(
      regions.filter(region => region.photoNumber === photoNumber).map(region => clampToPhoto(region))
    );
    return file && hasArea(box) ? [{ photoNumber, file, box }] : [];
  });
}

const segmentsOf = (segmentation: SegmentationData): PartImageSegments =>
  segmentsFromLabels(segmentation.regionMap, segmentation.width, segmentation.height);

export const boxOfRegions = (segmentation: SegmentationData, regions: Set<number>): PartImageItemBox | undefined =>
  regions.size > 0 ? outlineOfSegments([...regions], segmentsOf(segmentation)) : undefined;

export const cropRectOfBox = (box: PartImageItemBox, size: NaturalSize): CropRect => ({
  x: Math.round(box.left * size.width),
  y: Math.round(box.top * size.height),
  width: Math.round(box.width * size.width),
  height: Math.round(box.height * size.height),
});

const inPhoto = (value: number): number => Math.min(Math.max(value, 0), 1);

function boxBetween(left: number, top: number, right: number, bottom: number): PartImageItemBox | undefined {
  const box = { left: inPhoto(left), top: inPhoto(top), width: 0, height: 0 };
  box.width = inPhoto(right) - box.left;
  box.height = inPhoto(bottom) - box.top;
  return hasArea(box) ? box : undefined;
}

export const boxOfCropRect = (rect: CropRect | undefined, size: NaturalSize): PartImageItemBox | undefined =>
  rect &&
  boxBetween(
    rect.x / size.width,
    rect.y / size.height,
    (rect.x + rect.width) / size.width,
    (rect.y + rect.height) / size.height
  );

const MIN_LASSO_POINTS = 3;

function boxAround(points: LassoPoint[], size: NaturalSize): PartImageItemBox | undefined {
  const xs = points.map(point => point.x / size.width);
  const ys = points.map(point => point.y / size.height);
  return boxBetween(Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys));
}

export const boxOfLasso = (points: LassoPoint[], size: NaturalSize): PartImageItemBox | undefined =>
  points.length < MIN_LASSO_POINTS ? undefined : boxAround(points, size);
